// Listing status enum matching API
export enum ListingStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED',
}

// Sub-types
export interface BeforeAfterImage {
  before?: string;
  after?: string;
  /** @deprecated Use beforeCaption and afterCaption instead */
  caption?: string;
  /** Caption for the existing/before image */
  beforeCaption?: string;
  /** Caption for the after image */
  afterCaption?: string;
  /** Hide the Existing tag on the before image */
  hideBeforeTag?: boolean;
  /** Hide the After tag on the after image */
  hideAfterTag?: boolean;
}

export interface RenovationItem {
  item: string;
  isFeatured: boolean;
}

export interface RenovationOption {
  name: string;
  estimatedCost: number;
  images: string[];
  description?: string;
}

export interface ListingContractor {
  contractorId: string;
  renovationEstimate?: number;
  notes?: string;
  contractor?: {
    _id: string;
    name: string;
    companyName: string;
    profileLogo?: string;
    rating: number;
  };
}

export interface ListingLender {
  lenderId: string;
  lender?: {
    _id: string;
    name: string;
    companyName: string;
    profileLogo?: string;
    rating: number;
  };
}

export interface Location {
  lat: number;
  lng: number;
}

// Listing from API
export interface Listing {
  _id: string;
  uploadId: string;
  propertyTitle: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  neighborhood?: string;
  propertyTagline?: string;
  slug: string;
  location?: Location;
  isSuggested: boolean;
  isPublic: boolean;

  // Images
  heroImage?: string;
  beforeAfterImages: BeforeAfterImage[];
  renderingImages: string[];
  floorPlanImages: string[];

  // Pricing
  housePrice?: number;
  renovationPriceMin?: number | null;
  renovationPriceMax?: number | null;
  monthlyPaymentMin?: number;
  monthlyPaymentMax?: number;
  mortgageNotes?: string;

  // Renovation
  renovationScopeMin?: number | null;
  renovationScopeMax?: number | null;
  renovationItems: RenovationItem[];
  renovationOptions: RenovationOption[];

  // Property Details
  description?: string;
  locationDetails?: string;
  propertyDetails?: string;
  beds?: number;
  baths?: number;
  squareFeet?: number;
  yearBuilt?: number;
  lotSize?: string;
  detailedRenovationList?: string;

  // Relationships
  contractors: ListingContractor[];
  lenders: ListingLender[];

  // Timeline
  timeline?: string;

  // Status & Meta
  status: ListingStatus;
  createdAt: string;
  updatedAt: string;
}

// Input for creating a listing
export interface CreateListingInput {
  uploadId: string;
  propertyTitle: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  neighborhood?: string;
  propertyTagline?: string;
  slug?: string;
  location?: Location;
  isSuggested?: boolean;
  isPublic?: boolean;
  heroImage?: string;
  beforeAfterImages?: BeforeAfterImage[];
  renderingImages?: string[];
  floorPlanImages?: string[];
  housePrice?: number;
  renovationPriceMin?: number | null;
  renovationPriceMax?: number | null;
  monthlyPaymentMin?: number;
  monthlyPaymentMax?: number;
  mortgageNotes?: string;
  renovationScopeMin?: number | null;
  renovationScopeMax?: number | null;
  renovationItems?: RenovationItem[];
  renovationOptions?: RenovationOption[];
  description?: string;
  locationDetails?: string;
  propertyDetails?: string;
  beds?: number;
  baths?: number;
  squareFeet?: number;
  yearBuilt?: number;
  lotSize?: string;
  detailedRenovationList?: string;
  contractors?: ListingContractor[];
  lenders?: ListingLender[];
  timeline?: string;
  status?: ListingStatus;
}

// Input for updating a listing
export type UpdateListingInput = Partial<CreateListingInput>;

// Query params for listing listings
export interface ListingQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: ListingStatus;
  city?: string;
  state?: string;
  minPrice?: number;
  maxPrice?: number;
  minBeds?: number;
  minBaths?: number;
  search?: string;
}
